import { Theme } from '@mui/material/styles'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { configurePdfService } from 'siteline-common-web/services/pdf'
import { initDash0 } from 'siteline-common-web/utils/Dash0'
import { initializeLogRocket } from 'siteline-common-web/utils/LogRocket'
import { initSentry } from 'siteline-common-web/utils/Sentry'
import { App } from './App'
import './Fonts.js'
import { getCurrentUserAuthorization, initializeFirebaseAuth } from './client'
import {
  API_ENV,
  SENTRY_RELEASE,
  dash0AuthToken,
  dash0EndpointUrl,
  dash0ServiceName,
  logRocketApiUrl,
  logRocketProject,
  pdfBaseUrl,
  sentryDsn,
  sentryTunnel,
} from './common/config/constants'

// We need to import CSS from react-sizable as early as possible to ensure that annotations in the
// PDF editor are positioned correctly.
// See https://sitelinetech.slack.com/archives/CMY20J4S3/p1746721129266759
import 'react-resizable/css/styles.css'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface DefaultTheme extends Theme {}
}

configurePdfService({
  baseUrl: pdfBaseUrl,
  getAuth: getCurrentUserAuthorization,
})

initSentry({
  dsn: sentryDsn,
  tunnel: sentryTunnel,
  release: SENTRY_RELEASE,
  environment: API_ENV,
})

initDash0({
  serviceName: dash0ServiceName,
  authToken: dash0AuthToken,
  url: dash0EndpointUrl,
  version: SENTRY_RELEASE,
  environment: API_ENV,
})

// Initialize LogRocket session logging for prod and staging environments. Following install
// instructions using the app ID provided by LogRocket.
if (API_ENV === 'production' || API_ENV === 'staging') {
  void initializeLogRocket({
    project: logRocketProject,
    serverURL: logRocketApiUrl,
    release: SENTRY_RELEASE,
  })
}

initializeFirebaseAuth()

const container = document.getElementById('root')
if (!container) {
  throw new Error('Could not get root element')
}
const root = createRoot(container)
root.render(
  <StrictMode>
    <App />
  </StrictMode>
)
